import { useStoreState } from '@src/model/hooks';
import { usePagination } from '@src/store/hooks/usePagination';
import { useMyReactions } from '@src/store/reactions/hooks/useMyReactions';
import { useEffect, useMemo } from 'react';
import { type CommentCollectionCompositeKeyParams, type LoadMoreCommentsParams, commentCollectionAtomFamily, loadMoreCommentsAtom } from '../comments';

export interface UsePaginatedCommentsPayload extends CommentCollectionCompositeKeyParams {
    includeMyReactions?: boolean;
}

export const usePaginatedComments = (payload: UsePaginatedCommentsPayload) => {
    const { includeMyReactions = true } = payload;
    const entityType = 'Comment';

    const getParams = useMemo(
        () =>
            (payload: UsePaginatedCommentsPayload): CommentCollectionCompositeKeyParams & { fetchLimit: number } => ({
                type: payload.type,
                tokenName: payload.tokenName,
                fetchLimit: payload.fetchLimit ?? 10,
                ...(payload.postId && { postId: payload.postId }),
                ...(payload.userId && { userId: payload.userId }),
                ...(payload.parentCommentId && { parentCommentId: payload.parentCommentId }),
                includeDeleted: payload.includeDeleted,
            }),
        [],
    );

    const getLoadMoreParams = useMemo(
        () =>
            (params: CommentCollectionCompositeKeyParams & { fetchLimit?: number }, fetchLimit: number): LoadMoreCommentsParams => ({
                type: params.type,
                tokenName: params.tokenName,
                fetchLimit,
                ...(params.postId && { postId: params.postId }),
                ...(params.userId && { userId: params.userId }),
                ...(params.parentCommentId && { parentCommentId: params.parentCommentId }),
                includeDeleted: params.includeDeleted,
            }),
        [],
    );

    const { ids, loadMore, hasMore, loadStatus, error } = usePagination(payload, {
        collectionStateFamily: commentCollectionAtomFamily,
        loadMoreAtom: loadMoreCommentsAtom,
        getParams,
        getLoadMoreParams,
        entityType,
        includeMyReactions: includeMyReactions,
    });

    const myUserId = useStoreState(state => state.user.myUserId);
    const idsHash = useMemo(() => ids.join('-'), [ids]);
    const shouldLoadReactions = entityType && includeMyReactions && myUserId;
    const { loadMore: loadMoreReactions } = useMyReactions({
        entityType,
        entityIds: ids,
    });

    useEffect(() => {
        if (!shouldLoadReactions || !idsHash) return;
        loadMoreReactions();
    }, [idsHash, shouldLoadReactions]);

    return {
        commentIds: ids,
        loadMore,
        hasMore,
        loadStatus,
        error,
    };
};
